import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle2, XCircle, AlertCircle, RefreshCcw, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatusDot({ ok, dim = false }) {
  if (ok) return <CheckCircle2 size={14} className="text-[#4A6B53] flex-shrink-0" />;
  if (dim) return <AlertCircle size={14} className="text-[#D9A441] flex-shrink-0" />;
  return <XCircle size={14} className="text-[#C25934] flex-shrink-0 opacity-60" />;
}

function ScoreBar({ pct }) {
  const color = pct >= 80 ? '#4A6B53' : pct >= 50 ? '#D9A441' : '#C25934';
  return (
    <div className="w-full h-1.5 bg-[#F0EBE1] rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
    </div>
  );
}

export default function IntegrationStatusPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/superadmin/integrations-status`, { withCredentials: true });
      setData(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return (
    <div data-testid="integrations-status-loading" className="bg-white rounded-xl border border-[#E8E3D9] p-4 flex items-center justify-center">
      <Loader2 className="animate-spin text-[#C25934]" size={20} />
    </div>
  );

  if (!data) return null;

  const platformItems = [
    { key: 'emergent_llm', ...data.platform.emergent_llm },
    { key: 'resend', ...data.platform.resend },
    { key: 'google_cse', ...data.platform.google_cse },
    { key: 'stripe', ...data.platform.stripe },
  ];

  return (
    <div data-testid="integrations-status-panel" className="bg-white rounded-xl border border-[#E8E3D9] p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-[#2A2624] text-base" style={{ fontFamily: 'Outfit' }}>
          Stato Integrazioni
        </h3>
        <Button
          data-testid="integrations-refresh-btn"
          onClick={load}
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-[#877E78] hover:text-[#C25934]"
        >
          <RefreshCcw size={12} className="mr-1" /> Aggiorna
        </Button>
      </div>

      {/* Platform-wide */}
      <div>
        <p className="text-[10px] font-bold text-[#877E78] uppercase tracking-wider mb-2">Piattaforma</p>
        <div className="grid grid-cols-2 gap-2">
          {platformItems.map(it => (
            <div
              key={it.key}
              data-testid={`platform-${it.key}-status`}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${it.configured ? 'border-[#4A6B53]/20 bg-[#4A6B53]/5' : 'border-[#E8E3D9] bg-[#F0EBE1]/30'}`}
            >
              <StatusDot ok={it.configured} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-[#2A2624] truncate">{it.label}</p>
                {it.from_email && it.configured && (
                  <p className="text-[10px] text-[#877E78] truncate">{it.from_email}</p>
                )}
                {!it.configured && (
                  <p className="text-[10px] text-[#877E78]">Non configurato</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Per-shop */}
      <div>
        <p className="text-[10px] font-bold text-[#877E78] uppercase tracking-wider mb-2">
          Negozi ({data.shops.length})
        </p>
        <div className="space-y-2">
          {data.shops.map(shop => (
            <ShopRow key={shop.id} shop={shop} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ShopRow({ shop }) {
  const [open, setOpen] = useState(false);
  const intgs = shop.integrations;
  const order = ['telegram', 'stripe', 'paypal', 'bank_transfer', 'brand_kit', 'ai_plan', 'branding', 'subscription'];

  return (
    <div data-testid={`shop-status-${shop.id}`} className="border border-[#E8E3D9] rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-3 py-2 flex items-center gap-3 hover:bg-[#F0EBE1]/40 text-left"
        data-testid={`shop-status-toggle-${shop.id}`}
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[#2A2624] truncate">{shop.name}</p>
          <ScoreBar pct={shop.score.pct} />
        </div>
        <span className={`text-xs font-bold ${shop.score.pct >= 80 ? 'text-[#4A6B53]' : shop.score.pct >= 50 ? 'text-[#D9A441]' : 'text-[#C25934]'}`}>
          {shop.score.value}/{shop.score.total}
        </span>
        <span className="text-[#877E78] text-xs">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="border-t border-[#E8E3D9] px-3 py-2 grid grid-cols-2 gap-x-3 gap-y-1.5 bg-[#F0EBE1]/20">
          {order.map(k => {
            const it = intgs[k];
            if (!it) return null;
            return (
              <div key={k} className="flex items-center gap-1.5 text-[11px]">
                <StatusDot ok={it.configured} dim={!it.configured && it.enabled} />
                <span className="text-[#5C554F] truncate">{it.label}</span>
                {it.mode && it.configured && (
                  <span className="text-[9px] text-[#877E78] uppercase">{it.mode}</span>
                )}
                {it.scene_count > 0 && (
                  <span className="text-[9px] text-[#877E78]">{it.scene_count} scene</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
